import { useEffect, useMemo, useRef, useState } from 'react';
import { Label, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { PolarViewBoxRequired } from 'recharts/types/util/types';

import { cn } from '@/utils/classname';

export function PercentageChart({
  total,
  current,
  centerLabel,
  hidePercentage,
  doNotRoundCurrentValue,
  percentageClassName,
  displayCurrentValue,
}: {
  total: {
    name: string;
    value: number;
    lineClassName?: string;
  };
  current: {
    name: string;
    value: number;
    lineClassName?: string;
  };
  centerLabel?: string;
  hidePercentage?: boolean;
  doNotRoundCurrentValue?: boolean;
  percentageClassName?: string;
  displayCurrentValue?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  const [{ width, height }, setDimensions] = useState({
    width: 0,
    height: 0,
  });
  const smallestSize = width > height ? height : width;
  const containerStyle =
    smallestSize > 0 ? { width: smallestSize, height: smallestSize } : {};

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      setDimensions({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });

    resizeObserver.observe(containerRef.current);

    // Initial dimensions
    setDimensions({
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
    });

    return () => resizeObserver.disconnect();
  }, [containerRef]);

  const totalPie = [total];
  const currentPieData = [current];

  // Handle edge cases to prevent NaN or invalid calculations
  const percentage = useMemo(() => {
    if (!total.value || total.value === 0) return 0;
    if (!current.value || current.value === 0) return 0;

    const unRoundedPercentage = ((current.value / total.value) * 100).toFixed(
      1
    );
    return doNotRoundCurrentValue
      ? Number(unRoundedPercentage)
      : Math.round(Number(unRoundedPercentage));
  }, [current.value, total.value, doNotRoundCurrentValue]);

  return (
    <div className="size-full" ref={containerRef} style={containerStyle}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            dataKey="value"
            startAngle={90}
            endAngle={90 - (percentage * 360) / 100}
            data={currentPieData}
            outerRadius={'80%'}
            innerRadius={'70%'}
            stroke="none"
            cornerRadius={20}
            className={cn('fill-chariot-brand-primary', current.lineClassName)}
          />
          <Pie
            data={totalPie}
            dataKey="value"
            outerRadius={'100%'}
            innerRadius={'90%'}
            stroke="none"
            className={cn('fill-chariot-stroke', total.lineClassName)}
          >
            <Label
              content={({ viewBox }) => {
                if (!viewBox || typeof viewBox !== 'object') return null;
                const { cx, cy } = viewBox as PolarViewBoxRequired;

                // Use fallback center position if cx/cy are undefined
                const centerX = cx ?? smallestSize / 2;
                const centerY = cy ?? smallestSize / 2;

                return (
                  <>
                    <text
                      x={centerX}
                      y={centerY - (centerLabel ? 10 : 0)}
                      textAnchor="middle"
                      dominantBaseline="central"
                      className={cn(
                        'fill-chariot-text-primary font-bold',
                        'text-3xl',
                        percentageClassName
                      )}
                    >
                      {displayCurrentValue ? current.value : percentage}
                      {!hidePercentage && '%'}
                    </text>
                    {centerLabel && (
                      <text
                        x={centerX}
                        y={centerY + 15}
                        textAnchor="middle"
                        dominantBaseline="central"
                        className={cn(
                          'fill-chariot-text-secondary font-bold',
                          'text-sm'
                        )}
                      >
                        {centerLabel}
                      </text>
                    )}
                  </>
                );
              }}
              position="center"
            />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
